import * as THREE from 'three';

/**
 * EXPLODED VIEW & SUBSYSTEM HIGHLIGHTING // KEVIN ARTHUR DE LIMA
 * Manages mechanical axis displacement, leader line render, and subsystem focus.
 */

export function createExplodedController(groups, materials) {
  // Store default rest positions for all subsystems
  const restPositions = {
    coreFuselage: new THREE.Vector3(0, 0, 0),
    armsAndMotors: new THREE.Vector3(0, 0, 0),
    escStack: new THREE.Vector3(0, 0, 0),
    flightController: new THREE.Vector3(0, 0, 0),
    companionComputer: new THREE.Vector3(0, 0, 0),
    batteryPack: new THREE.Vector3(0, 0, 0),
    payloadBay: new THREE.Vector3(0, 0, 0)
  };

  // Exploded target displacement offsets
  const explodedOffsets = {
    coreFuselage: new THREE.Vector3(0, 0, 0),
    armsAndMotors: new THREE.Vector3(0, 0.4, 0), // Radial expansion handled per arm
    escStack: new THREE.Vector3(0, -0.6, 0),
    flightController: new THREE.Vector3(0, 0.9, 0),
    companionComputer: new THREE.Vector3(0, 1.8, 0),
    batteryPack: new THREE.Vector3(0, -1.5, 0),
    payloadBay: new THREE.Vector3(0, -2.6, 0)
  };

  // Create technical leader lines for exploded parts
  const leaderGroup = new THREE.Group();
  leaderGroup.name = "LeaderLines";
  groups.coreFuselage.parent.add(leaderGroup);

  const leaderPoints = [
    { start: new THREE.Vector3(0, 0, 0), end: new THREE.Vector3(0, 1.8, 0) },
    { start: new THREE.Vector3(0, 0, 0), end: new THREE.Vector3(0, -2.6, 0) }
  ];

  leaderPoints.forEach(p => {
    const geo = new THREE.BufferGeometry().setFromPoints([p.start, p.end]);
    const line = new THREE.Line(geo, materials.leaderLine);
    line.computeLineDistances();
    leaderGroup.add(line);
  });
  leaderGroup.visible = false;

  let currentExplodeRatio = 0;

  // Set explosion progress (0 = fully assembled, 1 = fully exploded)
  function setExplodeRatio(ratio) {
    currentExplodeRatio = THREE.MathUtils.clamp(ratio, 0, 1);
    leaderGroup.visible = currentExplodeRatio > 0.05;

    // Tween vertical stacks
    ['escStack', 'flightController', 'companionComputer', 'batteryPack', 'payloadBay'].forEach(key => {
      if (groups[key]) {
        groups[key].position.lerpVectors(
          restPositions[key],
          explodedOffsets[key],
          currentExplodeRatio
        );
      }
    });

    // Radial separation for arms
    if (groups.armsAndMotors && groups.armsAndMotors.children) {
      groups.armsAndMotors.children.forEach(arm => {
        const angle = arm.rotation.y;
        const radialShift = currentExplodeRatio * 0.8;
        arm.position.x = Math.cos(angle) * radialShift;
        arm.position.z = -Math.sin(angle) * radialShift;
      });
    }
  }

  // Highlight specific subsystem while dimming others
  function highlightSubsystem(targetPartKey) {
    const allParts = [
      { key: 'flightController', group: groups.flightController },
      { key: 'escStack', group: groups.escStack },
      { key: 'companionComputer', group: groups.companionComputer },
      { key: 'airframeArms', group: groups.armsAndMotors },
      { key: 'payloadBay', group: groups.payloadBay },
      { key: 'batteryPack', group: groups.batteryPack }
    ];

    allParts.forEach(({ key, group }) => {
      if (!group) return;
      const isTarget = targetPartKey === key || targetPartKey === 'all';
      const isDimmed = !isTarget && targetPartKey !== null;

      group.traverse(child => {
        if (child.isLineSegments || child.isLine) {
          if (isTarget) {
            child.material = materials.edgeHighlight;
          } else if (isDimmed) {
            child.material = materials.edgeDimmed;
          } else {
            child.material = materials.edgeLine;
          }
        }
      });
    });
  }

  function clearHighlight() {
    highlightSubsystem(null);
  }

  return {
    setExplodeRatio,
    highlightSubsystem,
    clearHighlight,
    leaderGroup
  };
}
